<script setup lang="ts">
  import { computed, getCurrentInstance, watch } from 'vue';
  import { useElementBounding } from '@vueuse/core';
  import PosUiAnchoredPopover from '@/components/pos-v2/ui/PosUiAnchoredPopover.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useAssistantCoachmark } from '@/composition/assistant/use-assistant-coachmark';
  import { isAssistantRtl } from '@/domain/assistant/assistant-direction';

  /**
   * «Su asistente» (plan §9.4): a one-time card under the top-bar pill that
   * says what the assistant does, with «Probar» (opens the panel) and «Ahora
   * no». Rendered at the app root and anchored to the pill. Nobody asked
   * for it, so it leaves the focus where it is; its text describes the
   * dialog for screen readers.
   */

  /** Gap between the pill and the card, where the arrow sits (px). */
  const GAP = 12;
  /** One coachmark in the app (V2AssistantRoot). */
  const TEXT_ID = 'v2-assistant-coachmark-text';
  /** Half the side of the arrow's rotated square (px). */
  const ARROW_HALF = 7;

  const vm = getCurrentInstance()!.proxy;
  const { visible, anchor, dismiss, tryIt, onClosed } = useAssistantCoachmark();
  const pillBox = useElementBounding(anchor);

  /** Arabic and Urdu punctuation must land at the right end of the text. */
  const textDir = computed(() =>
    isAssistantRtl(vm.$i18n.locale) ? 'rtl' : undefined
  );

  /**
   * The arrow points at the pill's centre and straddles the card's top edge.
   * `position: fixed` inside the card (no transform on it), so the card's
   * scroll box never clips it.
   */
  const arrowStyle = computed(() => ({
    left: `${pillBox.left.value + pillBox.width.value / 2 - ARROW_HALF}px`,
    top: `${pillBox.bottom.value + GAP - ARROW_HALF}px`,
  }));

  // The pill may have moved since it was last measured (the bar's content).
  watch(visible, (isVisible) => {
    if (isVisible) pillBox.update();
  });

  function onInput(open: boolean) {
    if (!open) onClosed();
  }
</script>

<template>
  <PosUiAnchoredPopover
    class="v2-assistant-coachmark"
    :value="visible"
    :anchor="anchor"
    :title="$t('assistant.coach.title')"
    placement="bottom"
    align="end"
    :width="320"
    :offset="GAP"
    :auto-focus="false"
    :described-by="TEXT_ID"
    @input="onInput"
  >
    <span class="coach-arrow" :style="arrowStyle" aria-hidden="true" />
    <PosUiBadge class="coach-badge" icon="creation">
      {{ $t('assistant.coach.badge') }}
    </PosUiBadge>
    <p class="coach-title" :dir="textDir">{{ $t('assistant.coach.title') }}</p>
    <p :id="TEXT_ID" class="coach-text" :dir="textDir">
      {{ $t('assistant.coach.text') }}
    </p>
    <div class="coach-actions">
      <PosUiButton color="neutral" variant="solid" @click="tryIt">
        {{ $t('assistant.coach.try') }}
      </PosUiButton>
      <PosUiButton
        class="coach-dismiss"
        color="neutral"
        variant="ghost"
        @click="dismiss"
      >
        {{ $t('assistant.coach.dismiss') }}
      </PosUiButton>
    </div>
  </PosUiAnchoredPopover>
</template>

<style scoped lang="scss">
  .pos-ui-anchored-popover.v2-assistant-coachmark {
    @apply tw-rounded-xl tw-p-4;

    .coach-arrow {
      @apply tw-fixed tw-h-3.5 tw-w-3.5 tw-bg-surface;
      transform: rotate(45deg);
      box-shadow: -1px -1px 0 rgb(var(--c-neutral-200));
    }

    .coach-title {
      @apply tw-mb-1 tw-mt-2 tw-text-base tw-font-semibold tw-text-neutral-900;
    }

    .coach-text {
      @apply tw-mb-0 tw-text-sm tw-leading-5 tw-text-neutral-600;
    }

    // Margins, not flex gap: old POS hardware (Chrome < 84).
    .coach-actions {
      @apply tw-mt-3.5 tw-flex tw-items-center;

      .coach-dismiss {
        @apply tw-ml-2;
      }
    }
  }
</style>
